import type { ComponentProps } from 'react';
import React, { forwardRef } from 'react';
import { ScrollView, StyleSheet, View } from 'react-native';
import { theme } from '@garnish/constants';

import {
  LOCATION_RESULTS_BORDER_RADIUS,
  LOCATION_RESULTS_THEME_BG_COLOR,
} from '../../LocationResultsV2.constants';
import { LocationResultsOtherLocationsTitle } from '../LocationResultsMoreLocationsTitle';
import {
  LocationResultsExtraScrollArea,
  LocationResultsList,
} from './subcomponents';

// ─────────────────────────────────────────────────────────────────────────────

export const LocationResultsLists = forwardRef<
  ScrollView,
  LocationResultsListsProps
>((props, ref) => {
  const {
    locationSearchType,
    locations = [],
    otherLocations = [],
    isExpanded,
    isLoading,
    withDetailsModal,
    containerHeightSharedValue,
    hasSingleResult,
    activeReorderRestaurantSlug,
    onCardPress,
    onCardLayout,
    onCardUnmount,
  } = props;

  const otherLocationsSearchType =
    locationSearchType === 'pickup' ? 'outpost' : 'pickup';

  const listsContainerStyles = [
    styles.listContainer,
    hasSingleResult ? styles.listContainerWithSingleResult : undefined,
  ];

  return (
    <ScrollView
      ref={ref}
      style={listsContainerStyles}
      scrollEnabled={isExpanded}
    >
      {/* ─── Main Locations ────────────────────────────────────────────────── */}

      {locations.length > 0 ? (
        <LocationResultsList
          locations={locations}
          locationSearchType={locationSearchType}
          withDetailsModal={withDetailsModal}
          activeReorderRestaurantSlug={activeReorderRestaurantSlug}
          onCardPress={onCardPress}
          onCardLayout={onCardLayout}
          onCardUnmount={onCardUnmount}
          isLoading={isLoading}
        />
      ) : null}

      {/* ─── Other Locations ───────────────────────────────────────────────── */}

      {otherLocations.length > 0 ? (
        <View style={styles.moreLocationsContainer}>
          <LocationResultsOtherLocationsTitle
            otherLocationSearchType={otherLocationsSearchType}
          />

          <LocationResultsList
            locations={otherLocations}
            locationSearchType={otherLocationsSearchType}
            withDetailsModal={withDetailsModal}
            onCardPress={onCardPress}
            onCardLayout={onCardLayout}
            onCardUnmount={onCardUnmount}
            isLoading={isLoading}
          />
        </View>
      ) : null}

      {/* ─── Extra Scroll Area ─────────────────────────────────────────────── */}

      {isExpanded ? null : (
        <LocationResultsExtraScrollArea
          containerHeightSharedValue={containerHeightSharedValue}
        />
      )}
    </ScrollView>
  );
});

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  listContainer: {
    backgroundColor: LOCATION_RESULTS_THEME_BG_COLOR,
  },
  listContainerWithSingleResult: {
    borderTopRightRadius: LOCATION_RESULTS_BORDER_RADIUS,
    borderTopLeftRadius: LOCATION_RESULTS_BORDER_RADIUS,
  },
  moreLocationsContainer: {
    borderTopWidth: 1,
    borderTopColor: theme.colors.LIGHT,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type LocationResultsListsProps = {
  readonly otherLocations?: LocationResultsListProps['locations'];
  readonly isExpanded: boolean;
  readonly hasSingleResult: boolean;
} & LocationResultsListProps &
  LocationResultsExtraScrollAreaProps;

type LocationResultsListProps = ComponentProps<typeof LocationResultsList>;

type LocationResultsExtraScrollAreaProps = ComponentProps<
  typeof LocationResultsExtraScrollArea
>;
